import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

type SupplierQuickCreateProps = {
  onSupplierCreated?: (supplierId: string) => void;
  trigger?: React.ReactNode;
};

const SupplierQuickCreate = ({ onSupplierCreated, trigger }: SupplierQuickCreateProps) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_info: '',
    city: '',
    shared_shipping_enabled: true,
    base_shipping_cost: 200,
  });

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('שם הספק הוא שדה חובה');
        return;
      }

      setSaving(true);

      const { data, error } = await supabase
        .from('suppliers')
        .insert([{
          name: formData.name.trim(),
          contact_info: formData.contact_info.trim() || null,
          city: formData.city.trim() || null,
          shared_shipping_enabled: formData.shared_shipping_enabled,
          base_shipping_cost: formData.base_shipping_cost,
          is_active: true,
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success('הספק נוסף בהצלחה');
      setOpen(false);
      setFormData({
        name: '',
        contact_info: '',
        city: '',
        shared_shipping_enabled: true,
        base_shipping_cost: 200,
      });

      if (onSupplierCreated && data) {
        onSupplierCreated(data.id);
      }
    } catch (error: any) {
      console.error('Error creating supplier:', error);
      if (error.code === '23505') {
        toast.error('ספק בשם זה כבר קיים במערכת');
      } else {
        toast.error('שגיאה ביצירת הספק');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {trigger ? (
        <div onClick={() => setOpen(true)}>{trigger}</div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Plus className="ml-2 h-4 w-4" />
          ספק חדש
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>יצירת ספק חדש</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="quick-name">שם הספק *</Label>
              <Input
                id="quick-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="לדוגמה: דלתא חומרי בניין"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quick-contact">פרטי קשר</Label>
                <Input
                  id="quick-contact"
                  value={formData.contact_info}
                  onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                  placeholder="טלפון"
                />
              </div>
              <div>
                <Label htmlFor="quick-city">עיר</Label>
                <Input
                  id="quick-city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="תל אביב"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="quick-shipping">עלות משלוח בסיסי (₪)</Label>
              <Input
                id="quick-shipping"
                type="number"
                value={formData.base_shipping_cost}
                onChange={(e) =>
                  setFormData({ ...formData, base_shipping_cost: parseFloat(e.target.value) || 0 })
                }
              />
            </div>

            <div className="flex items-center space-x-2 space-x-reverse">
              <Checkbox
                id="quick-shared"
                checked={formData.shared_shipping_enabled}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, shared_shipping_enabled: checked as boolean })
                }
              />
              <Label htmlFor="quick-shared" className="cursor-pointer text-sm">
                אפשר משלוח משותף (חיסכון ללקוח בהזמנת מספר מוצרים)
              </Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                ביטול
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'שומר...' : 'יצירה'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupplierQuickCreate;
